/**
 * Predictive / Manual campaign fields — stacked white tables for edit.
 * Sections: Campaign Information · Leads Selection · Schedule · Routing · AMD Detection
 *
 * variant "predictive": Dial Level + Agent Call Routing; Telephony IP + Media required; AMD
 * variant "manual": no Dial Level / AMD; Agent Call Routing on edit; Telephony IP + Media required
 */
import { useEffect, useMemo, useState } from "react";
import { CaretUpDown, Check } from "@phosphor-icons/react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import {
  CAMPAIGN_TYPES,
  AGENT_CALL_ROUTING,
  COMMON_TIMEZONES,
  normalizeAgentCallRouting,
  normalizeAgentLevelRouting,
  normalizeCallRecording,
  normalizeDialingPrefix,
  visibleInboundGroups,
  selectedOpensipsIds,
  selectedMediaServerIds,
} from "@/lib/campaignForm";
import FailoverServerMultiSelect from "@/components/FailoverServerMultiSelect";
import CampaignDispositionFields from "@/components/CampaignDispositionFields";

const ROUTING_LABELS = {
  random: "Random",
  less_calls: "Less Calls",
  high_calls: "High calls",
  agent_level: "Agent level",
};

const LEAD_STATUS_CODES = [
  { code: "NEW", label: "New" },
  { code: "QUEUE", label: "Queue" },
  { code: "DIALING", label: "Dialing" },
];

function Field({ label, required, hint, children, className = "", full = false }) {
  return (
    <div className={`space-y-2 ${full ? "sm:col-span-2" : ""} ${className}`}>
      {label && (
        <Label>
          {label}
          {required && <span className="text-destructive"> *</span>}
        </Label>
      )}
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

/** White card section matching the Predictive edit outline. */
function WhiteTable({ title, testid, children, cols = true }) {
  return (
    <section
      className="rounded-lg border border-border overflow-hidden bg-white shadow-sm"
      data-testid={testid}
    >
      <div className="border-b border-border bg-white px-4 py-2.5">
        <h3 className="text-sm font-semibold leading-none text-foreground">{title}</h3>
      </div>
      <div className={`bg-white p-4 ${cols ? "grid gap-4 sm:grid-cols-2" : "space-y-4"}`}>
        {children}
      </div>
    </section>
  );
}

function SmartMultiSelect({
  items,
  selectedIds,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No options",
  testid,
  getLabel = (item) => item.name || item.label || item.id,
  getId = (item) => item.id,
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => (Array.isArray(selectedIds) ? selectedIds : []),
    [selectedIds],
  );
  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const byId = useMemo(() => {
    const m = new Map();
    (items || []).forEach((it) => m.set(getId(it), it));
    return m;
  }, [items, getId]);

  const display = useMemo(() => {
    if (!selected.length) return placeholder;
    const names = selected.map((id) => getLabel(byId.get(id) || { id }));
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [selected, byId, placeholder, getLabel]);

  const toggle = (id) => {
    if (selectedSet.has(id)) onChange(selected.filter((x) => x !== id));
    else onChange([...selected, id]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3 bg-white"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {(items || []).map((it) => {
                const id = getId(it);
                const on = selectedSet.has(id);
                return (
                  <CommandItem
                    key={id}
                    value={`${getLabel(it)} ${id}`}
                    data-testid={`${testid}-option-${id}`}
                    onSelect={() => toggle(id)}
                  >
                    <Check className={`mr-2 h-3.5 w-3.5 shrink-0 ${on ? "opacity-100" : "opacity-0"}`} />
                    <span className="truncate">{getLabel(it)}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function TimezoneCombobox({ value, onChange, timezones, testid }) {
  const [open, setOpen] = useState(false);
  const zones = useMemo(() => {
    const base = Array.isArray(timezones) && timezones.length ? timezones : COMMON_TIMEZONES;
    const list = [...base];
    const cur = (value || "").trim();
    if (cur && !list.includes(cur)) list.unshift(cur);
    return list;
  }, [timezones, value]);
  const display = (value || "").trim() || "Select timezone…";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid={testid}
          className="w-full justify-between font-normal h-9 px-3 bg-white"
        >
          <span className="truncate">{display}</span>
          <CaretUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search timezone…" data-testid={`${testid}-search`} />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__none__ clear timezone"
                data-testid={`${testid}-option-none`}
                onSelect={() => { onChange(""); setOpen(false); }}
              >
                <Check className={`mr-2 h-3.5 w-3.5 ${!(value || "").trim() ? "opacity-100" : "opacity-0"}`} />
                <span className="text-muted-foreground">Account default</span>
              </CommandItem>
              {zones.map((z) => (
                <CommandItem
                  key={z}
                  value={z}
                  data-testid={`${testid}-option-${z.replace(/[^a-zA-Z0-9_-]/g, "_")}`}
                  onSelect={() => { onChange(z); setOpen(false); }}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${(value || "") === z ? "opacity-100" : "opacity-0"}`} />
                  {z}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function userLabel(u) {
  if (!u) return "";
  const name = (u.name || "").trim();
  const email = (u.email || "").trim();
  if (name && email) return `${name} (${email})`;
  return name || email || u.id;
}

function tid(prefix, name) {
  return `${prefix}-${name}`;
}

/**
 * @param {"edit"|"create"} mode — testid prefix
 * @param {"predictive"|"manual"} variant
 * @param {boolean} showSchedule — edit includes Schedule table
 * @param {boolean} [showAgentCallRouting] — default: predictive always; manual only on edit
 * @param {boolean} [showDialLevel] — default: predictive only
 * @param {boolean} [requireTelephonyIp] — default: manual only
 */
export default function PredictiveCampaignFields({
  mode = "edit",
  variant = "predictive",
  value,
  onChange,
  options = {},
  optionsError,
  onInboundGroupsReload,
  vmUploading = false,
  vmInputRef,
  onVmUploadClick,
  onVmFileChange,
  showSchedule = true,
  showAgentCallRouting,
  showDialLevel,
}) {
  const isManual = variant === "manual";
  const prefix = mode === "edit"
    ? (isManual ? "edit-manual" : "edit-predictive")
    : (isManual ? "manual" : "predictive");
  const showDial = showDialLevel ?? !isManual;
  const showRouting = showAgentCallRouting ?? (!isManual || mode === "edit");
  const showAmd = !isManual;
  const v = value || {};
  const set = (patch) => onChange(patch);
  const [dispositions, setDispositions] = useState([]);
  const [disposLoading, setDisposLoading] = useState(false);

  const {
    pdServers = [],
    assigned = [],
    mediaServers = [],
    inboundGroups = [],
    userGroups = [],
    teamUsers = [],
    leadLists = [],
    vendors = [],
    timezones = [],
  } = options;
  const opensipsIds = selectedOpensipsIds(v);
  const mediaIds = selectedMediaServerIds(v);

  const igVisible = visibleInboundGroups(inboundGroups, v.inboundGroupIds);
  const showInbound = v.campaignType === "inbound" || v.campaignType === "blended";
  const assignMode = v.assignMode === "user" ? "user" : "group";
  const recordingOn = normalizeCallRecording(v.callRecording, v.recordingEnabled) === "always";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setDisposLoading(true);
      try {
        const { data } = await api.get("/customer-settings/dispositions");
        if (cancelled) return;
        const system = Array.isArray(data?.system) ? data.system : [];
        const custom = Array.isArray(data?.custom) ? data.custom : [];
        const seen = new Set();
        const rows = [];
        for (const row of [...LEAD_STATUS_CODES, ...system, ...custom]) {
          const code = String(row.code || "").toUpperCase();
          if (!code || code === "DNC" || seen.has(code)) continue;
          seen.add(code);
          rows.push({
            code,
            label: row.label || code,
            source: row.source || (LEAD_STATUS_CODES.some((x) => x.code === code) ? "lead" : "system"),
          });
        }
        setDispositions(rows);
      } catch {
        if (!cancelled) {
          setDispositions(
            LEAD_STATUS_CODES.concat([
              { code: "ANSWERED", label: "Answered" },
              { code: "BUSY", label: "Busy" },
              { code: "NOANSWER", label: "No Answer" },
              { code: "FAILED", label: "Failed" },
              { code: "COMPLETED", label: "Completed" },
              { code: "CALLBACK", label: "Callback" },
            ]),
          );
        }
      } finally {
        if (!cancelled) setDisposLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const agentUsers = useMemo(
    () => (teamUsers || []).filter((u) => {
      const app = (u.app_type || "agent").toLowerCase();
      return app === "agent" || app === "manager";
    }),
    [teamUsers],
  );

  const toggleDialStatus = (code, checked) => {
    const cur = Array.isArray(v.dialStatuses) ? v.dialStatuses : [];
    set({
      dialStatuses: checked
        ? (cur.includes(code) ? cur : [...cur, code])
        : cur.filter((x) => x !== code),
    });
  };

  return (
    <div className="space-y-4" data-testid={tid(prefix, "form")}>
      {/* ── Campaign Information ─────────────────────────────── */}
      <WhiteTable title="Campaign Information" testid={tid(prefix, "section-info")} cols={false}>
        <div className={`grid gap-4 ${showDial ? "sm:grid-cols-2" : ""}`}>
          <Field label="Campaign name" required>
            <Input
              data-testid={tid(prefix, "name")}
              className="bg-white"
              value={v.name || ""}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="e.g. Sales Outbound"
            />
          </Field>
          {showDial && (
            <Field
              label="Dial Level"
              required
              hint="Lines per ready agent. Default is 1."
            >
              <Input
                data-testid={tid(prefix, "dial-level")}
                className="bg-white"
                type="number"
                min="0.1"
                step="0.1"
                required
                value={v.dialLevel ?? "1"}
                onChange={(e) => set({ dialLevel: e.target.value })}
                placeholder="1"
              />
            </Field>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Campaign type">
            <Select
              value={v.campaignType || "outbound"}
              onValueChange={(x) => set({
                campaignType: x,
                ...(x === "outbound" ? { inboundGroupIds: [] } : {}),
              })}
            >
              <SelectTrigger data-testid={tid(prefix, "campaign-type")} className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CAMPAIGN_TYPES.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t.charAt(0).toUpperCase() + t.slice(1)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>

          <Field
            label="Dialing prefix"
            hint="Digits agents dial from softphone to use this campaign's vendor."
          >
            <Input
              data-testid={tid(prefix, "dialing-prefix")}
              className="bg-white"
              inputMode="numeric"
              maxLength={8}
              value={v.dialingPrefix || ""}
              onChange={(e) => set({
                dialingPrefix: normalizeDialingPrefix(e.target.value),
              })}
              placeholder="e.g. 9"
            />
          </Field>
        </div>

        {showInbound && (
          <Field
            label="Inbound groups"
            hint={optionsError?.inboundGroups || "Shown for inbound and blended campaigns."}
          >
            <SmartMultiSelect
              items={igVisible}
              selectedIds={v.inboundGroupIds || []}
              onChange={(ids) => set({ inboundGroupIds: ids })}
              placeholder="Select inbound group(s)…"
              searchPlaceholder="Search inbound groups…"
              emptyText="No inbound groups — create them under Routing → Inbound Groups."
              testid={tid(prefix, "inbound-groups")}
            />
            {typeof onInboundGroupsReload === "function" && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="mt-1 h-7 px-2 text-xs"
                onClick={() => onInboundGroupsReload()}
                data-testid={tid(prefix, "inbound-groups-reload")}
              >
                Refresh groups
              </Button>
            )}
          </Field>
        )}

        <div className={`grid gap-4 ${showRouting ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
          <Field label="Callback scheduling">
            <Select
              value={v.callbackEnabled === false ? "no" : "yes"}
              onValueChange={(x) => set({ callbackEnabled: x === "yes" })}
            >
              <SelectTrigger data-testid={tid(prefix, "callback")} className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="yes">Yes</SelectItem>
                <SelectItem value="no">No</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          {showRouting && (
            <>
              <Field
                label="Agent Call Routing"
                hint="How logged-in Ready agents receive the next call."
              >
                <Select
                  value={normalizeAgentCallRouting(v.agentCallRouting)}
                  onValueChange={(x) => set({
                    agentCallRouting: x,
                    agentLevelRouting: x === "agent_level"
                      ? (normalizeAgentLevelRouting(v.agentLevelRouting, x) || "highest")
                      : "",
                  })}
                >
                  <SelectTrigger data-testid={tid(prefix, "agent-call-routing")} className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AGENT_CALL_ROUTING.map((r) => (
                      <SelectItem key={r} value={r}>{ROUTING_LABELS[r] || r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Agent level">
                <Select
                  value={
                    normalizeAgentCallRouting(v.agentCallRouting) === "agent_level"
                      ? (normalizeAgentLevelRouting(v.agentLevelRouting, "agent_level") || "highest")
                      : undefined
                  }
                  onValueChange={(x) => set({ agentLevelRouting: x })}
                  disabled={normalizeAgentCallRouting(v.agentCallRouting) !== "agent_level"}
                >
                  <SelectTrigger data-testid={tid(prefix, "agent-level")} className="bg-white">
                    <SelectValue placeholder="—" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="highest">Highest level</SelectItem>
                    <SelectItem value="lowest">Lowest level</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-[minmax(8rem,12rem)_1fr]">
          <Field label="Assigned to">
            <Select
              value={assignMode}
              onValueChange={(x) => set({
                assignMode: x,
                ...(x === "user"
                  ? { agentGroupIds: [] }
                  : { agentUserIds: [] }),
              })}
            >
              <SelectTrigger data-testid={tid(prefix, "assign-mode")} className="bg-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="group">Group</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label={assignMode === "user" ? "Select user(s)" : "Select group(s)"}>
            {assignMode === "user" ? (
              <SmartMultiSelect
                items={agentUsers}
                selectedIds={v.agentUserIds || []}
                onChange={(ids) => set({ agentUserIds: ids, agentGroupIds: [] })}
                placeholder="Select user(s)…"
                searchPlaceholder="Search users…"
                emptyText="No agents — create them under Users & Groups."
                testid={tid(prefix, "assign-users")}
                getLabel={userLabel}
              />
            ) : (
              <SmartMultiSelect
                items={userGroups}
                selectedIds={v.agentGroupIds || []}
                onChange={(ids) => set({ agentGroupIds: ids, agentUserIds: [] })}
                placeholder="Select group(s)…"
                searchPlaceholder="Search groups…"
                emptyText="No user groups — create them under Users & Groups."
                testid={tid(prefix, "assign-groups")}
              />
            )}
          </Field>
        </div>

        <div className="grid gap-4 sm:grid-cols-2 items-end">
          <Field label="Call recording">
            <div className="flex h-9 items-center justify-between gap-4 rounded-md border bg-white px-3">
              <span className="text-xs text-muted-foreground truncate">
                Record when the customer is connected
              </span>
              <Switch
                data-testid={tid(prefix, "recording")}
                checked={recordingOn}
                onCheckedChange={(on) => set({
                  callRecording: on ? "always" : "never",
                  ...(on ? {} : { recordingStartDelaySec: "" }),
                })}
              />
            </div>
          </Field>
          <Field
            label="Start Recording after (sec)"
            hint="Leave empty to start as soon as the call is connected."
          >
            <Input
              data-testid={tid(prefix, "recording-delay")}
              className="bg-white"
              type="number"
              min="1"
              max="3600"
              disabled={!recordingOn}
              value={recordingOn ? (v.recordingStartDelaySec || "") : ""}
              onChange={(e) => set({ recordingStartDelaySec: e.target.value })}
              placeholder={recordingOn ? "" : "—"}
            />
          </Field>
        </div>
      </WhiteTable>

      {/* ── Leads Selection ──────────────────────────────────── */}
      <WhiteTable title="Leads Selection" testid={tid(prefix, "section-leads")} cols={false}>
        <Field label="Select Lists" hint="Lead lists this campaign may dial.">
          <SmartMultiSelect
            items={leadLists}
            selectedIds={v.listIds || []}
            onChange={(ids) => set({ listIds: ids })}
            placeholder="Select list(s)…"
            searchPlaceholder="Search lists…"
            emptyText="No lists yet — create them under Lists."
            testid={tid(prefix, "lists")}
            getLabel={(l) => (
              l.lead_count != null ? `${l.name} (${l.lead_count})` : (l.name || l.id)
            )}
          />
        </Field>

        <CampaignDispositionFields
          value={v}
          onChange={set}
          testidPrefix={tid(prefix, "dispos")}
        />
      </WhiteTable>

      {/* ── Schedule ─────────────────────────────────────────── */}
      {showSchedule && (
        <WhiteTable title="Schedule" testid={tid(prefix, "section-schedule")}>
          <Field
            label="Timezone"
            hint="Schedule times use this timezone (or account default)."
            full
          >
            <TimezoneCombobox
              value={v.timezone || ""}
              onChange={(x) => set({ timezone: x })}
              timezones={timezones}
              testid={tid(prefix, "timezone")}
            />
          </Field>
          <Field label="Schedule start (HH:MM)">
            <Input
              type="time"
              className="bg-white"
              value={v.scheduleStart || ""}
              onChange={(e) => set({ scheduleStart: e.target.value })}
              data-testid={tid(prefix, "schedule-start")}
            />
          </Field>
          <Field label="Schedule end (HH:MM)">
            <Input
              type="time"
              className="bg-white"
              value={v.scheduleEnd || ""}
              onChange={(e) => set({ scheduleEnd: e.target.value })}
              data-testid={tid(prefix, "schedule-end")}
            />
          </Field>
          <p className="text-xs text-muted-foreground sm:col-span-2" data-testid={tid(prefix, "schedule-247-hint")}>
            Leave both blank to run 24×7. Outside the window the campaign cannot start and an active campaign is auto-stopped.
          </p>
        </WhiteTable>
      )}

      {/* ── Routing ──────────────────────────────────────────── */}
      <WhiteTable title="Routing" testid={tid(prefix, "section-routing")} cols={false}>
        <Field
          label="Select PD servers"
          required={isManual || pdServers.length !== 1}
          hint="First selected is Primary; others are backups if the primary is down or at capacity."
        >
          <FailoverServerMultiSelect
            servers={pdServers}
            selectedIds={
              (v.pdServerIds || []).length
                ? v.pdServerIds
                : (v.pdServerId ? [v.pdServerId] : [])
            }
            onChange={(ids) => set({ pdServerIds: ids, pdServerId: ids[0] || "" })}
            testid={tid(prefix, "pd-servers")}
            emptyText="No PD servers assigned to this account."
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="Telephony IP"
            required
            hint="First selected is Primary; others are backups if the primary is down or at capacity."
          >
            <FailoverServerMultiSelect
              servers={assigned}
              selectedIds={opensipsIds}
              onChange={(ids) => set({ opensipsIds: ids, opensipsId: ids[0] || "__none__" })}
              testid={tid(prefix, "telephony-ip")}
              emptyText="No Telephony IP servers assigned to this account."
            />
          </Field>
          <Field
            label="Media server"
            required
            hint="First selected is Primary; others are backups if the primary is down or at capacity."
          >
            <FailoverServerMultiSelect
              servers={mediaServers}
              selectedIds={mediaIds}
              onChange={(ids) => set({ mediaServerIds: ids, mediaServerId: ids[0] || "" })}
              testid={tid(prefix, "media-servers")}
              emptyText="No media servers assigned to this account."
            />
          </Field>
        </div>

        <Field
          label="Vendor route"
          required
          hint="Required. Campaign-selected vendor only — no outbound vendor failover."
        >
          <Select
            value={v.vendorId && v.vendorId !== "__none__" ? v.vendorId : undefined}
            onValueChange={(x) => set({ vendorId: x })}
          >
            <SelectTrigger data-testid={tid(prefix, "vendor")} className="bg-white">
              <SelectValue placeholder="Select vendor route…" />
            </SelectTrigger>
            <SelectContent>
              {vendors.map((x) => (
                <SelectItem key={x.id} value={x.id}>{x.name} · {x.host}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {vendors.length === 0 && (
            <p className="text-xs text-destructive" data-testid={tid(prefix, "vendor-empty")}>
              No active vendor routes — create one under Routing → Vendors.
            </p>
          )}
        </Field>
      </WhiteTable>

      {/* ── AMD Detection (Predictive only) ───────────────────── */}
      {showAmd && (
        <WhiteTable title="AMD Detection" testid={tid(prefix, "section-amd")} cols={false}>
          <div className="flex items-center justify-between gap-4 rounded-md border bg-white px-3 py-2">
            <div>
              <Label className="text-sm">AMD Detection</Label>
              <p className="text-[11px] text-muted-foreground">
                Classify human vs answering machine on connect.
              </p>
            </div>
            <Switch
              data-testid={tid(prefix, "amd-enabled")}
              checked={!!v.amdEnabled}
              onCheckedChange={(x) => set({ amdEnabled: x })}
            />
          </div>

          {v.amdEnabled && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Decision window (ms)">
                <Input
                  data-testid={tid(prefix, "amd-timeout")}
                  className="bg-white"
                  type="number"
                  min="1000"
                  max="8000"
                  step="100"
                  value={v.amdTimeout || "3200"}
                  onChange={(e) => set({ amdTimeout: e.target.value })}
                />
              </Field>

              <Field label="On voicemail">
                <Select
                  value={
                    v.amdAction === "voicemail" || v.amdAction === "log_only"
                      ? v.amdAction
                      : "hangup"
                  }
                  onValueChange={(x) => set({ amdAction: x })}
                >
                  <SelectTrigger data-testid={tid(prefix, "amd-action")} className="bg-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hangup">Hangup</SelectItem>
                    <SelectItem value="voicemail">Leave voicemail</SelectItem>
                    <SelectItem value="log_only">Log only</SelectItem>
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Voicemail Max (ms)">
                <Input
                  data-testid={tid(prefix, "amd-greeting-max")}
                  className="bg-white"
                  type="number"
                  min="2000"
                  max="12000"
                  step="100"
                  value={v.amdGreetingMax || "4500"}
                  onChange={(e) => set({ amdGreetingMax: e.target.value })}
                />
              </Field>

              {v.amdAction === "voicemail" && (
                <Field label="Voicemail drop audio" required className="sm:col-span-2">
                  <input
                    ref={vmInputRef}
                    type="file"
                    accept="audio/*,.mp3,.wav,.ogg,.m4a,.aac,.webm"
                    hidden
                    onChange={onVmFileChange}
                    data-testid={tid(prefix, "vm-file-input")}
                  />
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="bg-white"
                      disabled={vmUploading}
                      onClick={onVmUploadClick}
                      data-testid={tid(prefix, "vm-upload")}
                    >
                      {vmUploading ? "Uploading…" : (v.vmAudioId ? "Replace audio" : "Upload audio")}
                    </Button>
                    {v.vmAudioId ? (
                      <span className="text-xs text-emerald-600 truncate" data-testid={tid(prefix, "vm-filename")}>
                        {v.vmFilename || "Voicemail audio attached"}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No file uploaded yet</span>
                    )}
                  </div>
                </Field>
              )}
            </div>
          )}
        </WhiteTable>
      )}
    </div>
  );
}
